import {createReduxStore, register, useDispatch, useSelect} from '@wordpress/data';
import {apiFetch as API_FETCH, controls as dataControls, select as SELECT} from '@wordpress/data-controls';
import {PRODUCT_DETAILS_STORE_KEY} from '../constants';

const DEFAULT_STATE = {
    sku: null,
    source: 'BEST_BUY',

    entity: null,
    loading: false,
    error: null,

    recs: {alsoBought: [], alsoViewed: []},
    recsLoading: false,
    recsError: null,

    subcategories: [],
    subcategoriesLoading: false,
};

const AT = {
    SET_SKU: 'SET_SKU',
    SET_LOADING: 'SET_LOADING',
    SET_ERROR: 'SET_ERROR',
    SET_ENTITY: 'SET_ENTITY',

    SET_RECS_LOADING: 'SET_RECS_LOADING',
    SET_RECS_ERROR: 'SET_RECS_ERROR',
    SET_RECS: 'SET_RECS',

    SET_SUBCATEGORIES: 'SET_SUBCATEGORIES',
    SET_SUBCATEGORIES_LOADING: 'SET_SUBCATEGORIES_LOADING',
};

const actions = {
    setSku(sku, source = 'BEST_BUY') {
        return {type: AT.SET_SKU, sku: sku ? String(sku) : null, source: source || 'BEST_BUY'};
    },

    * loadProduct({sku, source = 'BEST_BUY'} = {}) {

        if (sku) {
            yield actions.setSku(String(sku), source || 'BEST_BUY');
        }

        yield {type: AT.SET_LOADING, loading: true};
        yield {type: AT.SET_RECS_LOADING, loading: true};

        const s = sku || (yield SELECT(PRODUCT_DETAILS_STORE_KEY, 'getSku'));
        if (!s) {
            yield {type: AT.SET_ERROR, error: 'Missing SKU'};
            yield {type: AT.SET_RECS_ERROR, error: 'Missing SKU'};
            yield {type: AT.SET_LOADING, loading: false};
            yield {type: AT.SET_RECS_LOADING, loading: false};
            return;
        }

        try {
            const path = `/api/products/${encodeURIComponent(s)}`;
            const entity = yield API_FETCH({path, method: 'GET'});
            yield {type: AT.SET_ENTITY, entity};
            yield {type: AT.SET_ERROR, error: null};
        } catch (e) {
            yield {type: AT.SET_ERROR, error: e?.message || 'Failed to load product'};
        } finally {
            yield {type: AT.SET_LOADING, loading: false};
        }

        try {
            const path = `/api/products/${encodeURIComponent(s)}/recommendations`;
            const res = yield API_FETCH({path, method: 'GET'});
            const alsoBought = Array.isArray(res?.alsoBought) ? res.alsoBought.filter(Boolean) : [];
            const alsoViewed = Array.isArray(res?.alsoViewed) ? res.alsoViewed.filter(Boolean) : [];
            const seen = new Set(alsoBought.map(p => String(p?.sku)));
            const mergedViewed = alsoViewed.filter(p => !seen.has(String(p?.sku)));
            yield {type: AT.SET_RECS, recs: {alsoBought, alsoViewed: mergedViewed}};
            yield {type: AT.SET_RECS_ERROR, error: null};
        } catch (e) {
            yield {type: AT.SET_RECS_ERROR, error: e?.message || 'Failed to load recommendations'};
        } finally {
            yield {type: AT.SET_RECS_LOADING, loading: false};
        }

        const entity = yield SELECT(PRODUCT_DETAILS_STORE_KEY, 'getProduct');
        const catId = entity?.data?.primaryCategory?.id || entity?.categoryId;

        if (catId) {
            yield {type: AT.SET_SUBCATEGORIES_LOADING, loading: true};
            try {
                const path = `/api/categories/by-woocommerce-id/${encodeURIComponent(catId)}`;
                const res = yield API_FETCH({path, headers: {'X-WP-Nonce': epiData.nonce}});
                const list = res?.categories || res?.subCategories || [];
                yield {type: AT.SET_SUBCATEGORIES, subcategories: list};
            } catch (e) {
                console.error("Failed to load subcategories", e);
            } finally {
                yield {type: AT.SET_SUBCATEGORIES_LOADING, loading: false};
            }
        }
    },
};

function reducer(state = DEFAULT_STATE, action) {
    switch (action.type) {
        case AT.SET_SKU:
            return {
                ...state,
                sku: action.sku,
                source: action.source || 'BEST_BUY',

                entity: null,
                error: null,
                loading: false,
                recs: {alsoBought: [], alsoViewed: []},
                recsLoading: false,
                recsError: null,
            };
        case AT.SET_LOADING:
            return {...state, loading: !!action.loading};
        case AT.SET_ERROR:
            return {...state, error: action.error || null};
        case AT.SET_ENTITY:
            return {...state, entity: action.entity || null};
        case AT.SET_RECS_LOADING:
            return {...state, recsLoading: !!action.loading};
        case AT.SET_RECS_ERROR:
            return {...state, recsError: action.error || null};
        case AT.SET_RECS:
            return {...state, recs: action.recs || {alsoBought: [], alsoViewed: []}};
        case AT.SET_SUBCATEGORIES:
            return {...state, subcategories: action.subcategories || []};
        case AT.SET_SUBCATEGORIES_LOADING:
            return {...state, subcategoriesLoading: !!action.loading};
        default:
            return state;
    }
}

const selectors = {
    getSku: (state) => state.sku,
    getSource: (state) => state.source,
    getProduct: (state) => state.entity || {},
    getProductData: (state) => (state.entity?.data) || {},
    isLoading: (state) => !!state.loading,
    getError: (state) => state.error,
    getRecommendations: (state) => state.recs,
    isRecommendationsLoading: (state) => !!state.recsLoading,
    getRecommendationsError: (state) => state.recsError,
    getSubcategories: (state) => state.subcategories,
    isSubcategoriesLoading: (state) => !!state.subcategoriesLoading,
};

const controls = {
    ...dataControls,
};

export const store = createReduxStore(PRODUCT_DETAILS_STORE_KEY, {
    reducer,
    actions,
    selectors,
    controls,
});

register(store);

export const useProductDetailsDispatch = () => useDispatch(PRODUCT_DETAILS_STORE_KEY);
export const useProductDetailsSelector = () => useSelect(
    (select) => {
        const s = select(PRODUCT_DETAILS_STORE_KEY);
        return s ? {
            sku: s.getSku(),
            source: s.getSource(),
            product: s.getProduct(),
            data: s.getProductData(),
            loading: s.isLoading(),
            error: s.getError(),
            recs: s.getRecommendations(),
            recsLoading: s.isRecommendationsLoading(),
            recsError: s.getRecommendationsError(),
            subcategories: s.getSubcategories(),
            subcategoriesLoading: s.isSubcategoriesLoading(),
        } : DEFAULT_STATE;
    },
    []
);

export const useProductDetails = () => {
    const dispatch = useProductDetailsDispatch();
    const selector = useProductDetailsSelector();
    return {...dispatch, ...selector};
};
